import React, { useState, useRef } from 'react';
import Tooltip from '@material-ui/core/Tooltip';
import { useDispatch } from 'react-redux';
import { alpha, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { toast } from 'react-toastify';
import Box from '@mui/material/Box';
import AppBar from '@mui/material/AppBar';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ClearIcon from '@mui/icons-material/Clear';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { DateRangePicker } from 'react-date-range';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import { format } from 'date-fns';
import { enUS } from 'date-fns/locale';
import Container from '../../GeneralComponents/Container';
import { Topbar, Sidebar, Table } from '../components';
import setGlobalLoader from '../../../redux/actions/loaderActions';
import { getFuelCardApplications, updateFuelCardApplication } from '../../../redux/actions/apiActions';
import * as XLSX from 'xlsx';

const FUEL_CARD_APPLICATION_STATUS = {
  PENDING: 'PENDING',
  UNDER_REVIEW: 'UNDER_REVIEW',
  SUBMITTED: 'SUBMITTED',
  ISSUED: 'ISSUED',
  REJECTED: 'REJECTED',
};

const STATUS_LABEL = {
  PENDING: 'Pending',
  UNDER_REVIEW: 'Under Review',
  SUBMITTED: 'Submitted',
  ISSUED: 'Issued',
  REJECTED: 'Rejected',
};

const STATUS_COLOR = {
  PENDING: 'warning',
  UNDER_REVIEW: 'info',
  SUBMITTED: 'info',
  ISSUED: 'success',
  REJECTED: 'error',
};

const DocumentImage = ({ src, alt, slotLabel }) => (
  <Box sx={{ mb: 1.5 }}>
    {slotLabel && (
      <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 0.5 }}>
        {slotLabel}
      </Typography>
    )}
    <Box sx={{ position: 'relative', display: 'inline-block' }}>
      <Box
        component="img"
        src={src}
        alt={alt}
        sx={{
          width: '100%',
          maxWidth: 280,
          height: 140,
          objectFit: 'cover',
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'divider',
          display: 'block',
        }}
      />
      <Tooltip title="Open full image">
        <Box
          component="a"
          href={src}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            position: 'absolute',
            top: 6,
            right: 6,
            bgcolor: 'rgba(0,0,0,0.55)',
            borderRadius: '50%',
            width: 28,
            height: 28,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' },
          }}
        >
          <OpenInNewIcon sx={{ fontSize: 16 }} />
        </Box>
      </Tooltip>
    </Box>
  </Box>
);

// imageUrls: string[] — up to 2 for aadhaar/vehicleRC, 1 for PAN
// slotLabels: string[] — e.g. ['Front', 'Back']
const DocumentCard = ({ label, number, imageUrls, slotLabels }) => {
  const urls = Array.isArray(imageUrls) ? imageUrls.filter(Boolean) : [];
  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        p: 2,
      }}
    >
      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
        {label}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Number:&nbsp;
        <Typography component="span" variant="body2" fontWeight={600} color="text.primary">
          {number || '—'}
        </Typography>
      </Typography>
      {urls.length > 0 ? (
        urls.map((url, i) => (
          <DocumentImage
            key={i}
            src={url}
            alt={`${label} ${slotLabels?.[i] ?? ''}`}
            slotLabel={slotLabels?.[i]}
          />
        ))
      ) : (
        <Typography variant="caption" color="text.secondary">
          No image uploaded
        </Typography>
      )}
    </Box>
  );
};

const TableComponent = React.forwardRef(({ columns, data, title, isRemoteData }, ref) => {
  const theme = useTheme();
  return (
    <Box p={4}>
      <Box
        width={1}
        height={1}
        borderRadius={2}
        border={`2px solid ${theme.palette.divider}`}
        sx={{ borderStyle: 'dashed' }}
      >
        <Table
          ref={ref}
          columns={columns}
          data={data}
          export={false}
          selection={false}
          toolbar
          title={title}
          isRemoteData={isRemoteData}
        />
      </Box>
    </Box>
  );
});

const FuelCardApplications = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const tableRef = useRef(null);
  const isMd = useMediaQuery(theme.breakpoints.up('md'), { defaultMatches: true });

  const [openSidebar, setOpenSidebar] = useState(false);
  const open = isMd ? false : openSidebar;

  // Date filter
  const today = new Date();
  const minDateObj = new Date('2025-01-01');
  const [dateRange, setDateRange] = useState([{ startDate: null, endDate: null, key: 'selection' }]);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const startDate = dateRange[0].startDate;
  const endDate = dateRange[0].endDate;

  // Status filter
  const [filterStatus, setFilterStatus] = useState('');

  // View dialog
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewApplication, setViewApplication] = useState(null);

  // Status update dialog
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [newStatus, setNewStatus] = useState('');
  const [reviewNotes, setReviewNotes] = useState('');
  const [updating, setUpdating] = useState(false);

  const openViewDialog = (rowData) => {
    setViewApplication(rowData);
    setViewDialogOpen(true);
  };

  const openStatusDialog = (rowData) => {
    setSelectedApplication(rowData);
    setNewStatus(rowData.applicationStatus || FUEL_CARD_APPLICATION_STATUS.PENDING);
    setReviewNotes(rowData.reviewNotes || '');
    setStatusDialogOpen(true);
  };

  const handleStatusUpdate = async () => {
    if (!newStatus) {
      toast.error('Please select a status.');
      return;
    }
    if (!reviewNotes || !reviewNotes.trim()) {
      toast.error('Review notes are required.');
      return;
    }
    setUpdating(true);
    try {
      await updateFuelCardApplication(selectedApplication._id, {
        applicationStatus: newStatus,
        reviewNotes: reviewNotes || null,
      });
      toast.success('Application status updated successfully');
      setStatusDialogOpen(false);
      if (tableRef.current) tableRef.current.onQueryChange();
    } catch (error) {
      console.log('update fuel card status error', error);
      toast.error(error.response?.data?.msg ?? 'Error updating application status');
    } finally {
      setUpdating(false);
    }
  };

  const columns = [
    { title: 'Sr No.', field: 'index', filtering: false },
    { title: 'Organization', field: 'organizationName', filtering: false },
    { title: 'Contact Name', field: 'contactName', filtering: false },
    { title: 'Contact Phone', field: 'contactPhone', filtering: false },
    { title: 'Contact Email', field: 'contactEmail', filtering: false },
    { title: 'PAN', field: 'panNumber', filtering: false },
    { title: 'Aadhaar', field: 'aadhaarNumber', filtering: false },
    { title: 'Vehicle RC', field: 'vehicleRCNumber', filtering: false },
    {
      title: 'Status',
      field: 'applicationStatus',
      filtering: false,
      render: (rowData) => (
        <Chip
          label={STATUS_LABEL[rowData.applicationStatus] ?? rowData.applicationStatus}
          color={STATUS_COLOR[rowData.applicationStatus] ?? 'default'}
          size="small"
          sx={{ fontWeight: 700 }}
        />
      ),
    },
    { title: 'Applied On', field: 'appliedOn', filtering: false },
    {
      title: 'Actions',
      field: '',
      filtering: false,
      render: (rowData) => (
        <div className="tableActions">
          <Tooltip title="View Documents">
            <VisibilityIcon
              sx={{ cursor: 'pointer' }}
              onClick={() => openViewDialog(rowData)}
            />
          </Tooltip>
          <Tooltip title="Update Status">
            <EditIcon
              sx={{ cursor: 'pointer' }}
              onClick={() => openStatusDialog(rowData)}
            />
          </Tooltip>
        </div>
      ),
    },
  ];

  const fetchApplications = async (query) => {
    return new Promise(async (resolve) => {
      try {
        dispatch(setGlobalLoader(true));
        const page = query.page + 1;
        const limit = query.pageSize;

        const queryParams = new URLSearchParams({
          page: page.toString(),
          limit: limit.toString(),
        });

        if (filterStatus) queryParams.append('applicationStatus', filterStatus);
        if (startDate) queryParams.append('startDate', format(startDate, 'yyyy-MM-dd'));
        if (endDate) queryParams.append('endDate', format(endDate, 'yyyy-MM-dd'));

        const res = await getFuelCardApplications(`?${queryParams.toString()}`);
        const apiData = res?.data?.data;

        const data = apiData?.docs?.map((d, i) => ({
          ...d,
          index: (page - 1) * limit + i + 1,
          organizationName: d?.applicantId?.name ?? '—',
          panNumber: d?.pan?.number ?? '—',
          aadhaarNumber: d?.aadhaar?.number ?? '—',
          vehicleRCNumber: d?.vehicleRC?.number ?? '—',
          appliedOn: d?.createdAt ? format(new Date(d.createdAt), 'MMM dd, yyyy') : '—',
        }));

        dispatch(setGlobalLoader(false));
        resolve({
          data: data,
          page: query.page,
          totalCount: apiData?.total || 0,
        });
      } catch (error) {
        dispatch(setGlobalLoader(false));
        console.log('get fuel card applications error', error);
        toast.error(error?.response?.data?.msg || 'Error fetching fuel card applications');
        resolve({ data: [], page: 0, totalCount: 0 });
      }
    });
  };

  return (
    <Box>
      <AppBar
        position="fixed"
        sx={{
          backgroundColor: theme.palette.background.paper,
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        }}
        elevation={0}
      >
        <Container maxWidth={1} paddingY={{ xs: 1, sm: 1.5 }}>
          <Topbar onSidebarOpen={() => setOpenSidebar(true)} />
        </Container>
      </AppBar>
      <Sidebar
        onClose={() => setOpenSidebar(false)}
        open={open}
        variant={isMd ? 'permanent' : 'temporary'}
      />
      <main>
        <Box height={{ xs: 94, sm: 102, md: 107 }} />
        <Box display="flex" flex="1 1 auto" overflow="hidden" paddingLeft={{ md: '256px' }}>
          <Box display="flex" flex="1 1 auto" overflow="hidden">
            <Box flex="1 1 auto" height="100%" overflow="auto">
              <Stack marginTop={3} mx={{ xs: 3, md: 4 }} gap={2}>
                {/* Row 1: filters left, download right */}
                <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
                  <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
                    {/* Date range picker */}
                    <Box sx={{ position: 'relative' }}>
                      <TextField
                        size="small"
                        value={
                          startDate && endDate
                            ? `${format(startDate, 'MMM dd, yyyy')} - ${format(endDate, 'MMM dd, yyyy')}`
                            : ''
                        }
                        placeholder="Select date range"
                        onClick={() => setShowDatePicker(!showDatePicker)}
                        InputProps={{
                          readOnly: true,
                          startAdornment: (
                            <InputAdornment position="start">
                              <CalendarMonthIcon />
                            </InputAdornment>
                          ),
                          endAdornment: startDate && endDate && (
                            <InputAdornment position="end">
                              <ClearIcon
                                sx={{ cursor: 'pointer' }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setDateRange([{ startDate: null, endDate: null, key: 'selection' }]);
                                  setShowDatePicker(false);
                                }}
                              />
                            </InputAdornment>
                          ),
                        }}
                        sx={{ minWidth: 280, cursor: 'pointer' }}
                      />
                      {showDatePicker && (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            zIndex: 1000,
                            mt: 1,
                            boxShadow: 3,
                            borderRadius: 1,
                            bgcolor: 'background.paper',
                          }}
                        >
                          <DateRangePicker
                            onChange={(item) => setDateRange([item.selection])}
                            ranges={[{
                              startDate: dateRange[0].startDate || new Date(),
                              endDate: dateRange[0].endDate || new Date(),
                              key: 'selection',
                            }]}
                            minDate={minDateObj}
                            maxDate={today}
                            rangeColors={[theme.palette.primary.main]}
                            locale={enUS}
                          />
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1, borderTop: `1px solid ${theme.palette.divider}` }}>
                            <Button size="small" onClick={() => setShowDatePicker(false)}>Close</Button>
                          </Box>
                        </Box>
                      )}
                    </Box>

                    {/* Status dropdown */}
                    <TextField
                      select
                      label="Status"
                      size="small"
                      value={filterStatus}
                      onChange={(e) => setFilterStatus(e.target.value)}
                      sx={{ minWidth: 160 }}
                    >
                      <MenuItem value="">All Statuses</MenuItem>
                      {Object.entries(FUEL_CARD_APPLICATION_STATUS).map(([key, value]) => (
                        <MenuItem key={key} value={value}>{STATUS_LABEL[value]}</MenuItem>
                      ))}
                    </TextField>

                    <Button
                      variant="outlined"
                      color="primary"
                      size="medium"
                      onClick={() => { if (tableRef.current) tableRef.current.onQueryChange(); }}
                    >
                      Filter
                    </Button>
                    <Button
                      variant="text"
                      color="error"
                      size="medium"
                      onClick={() => {
                        setDateRange([{ startDate: null, endDate: null, key: 'selection' }]);
                        setFilterStatus('');
                        setShowDatePicker(false);
                        setTimeout(() => { if (tableRef.current) tableRef.current.onQueryChange(); }, 100);
                      }}
                    >
                      Reset
                    </Button>
                  </Box>

                  <Button
                  variant="outlined"
                  color="primary"
                  component="button"
                  size="medium"
                  onClick={async () => {
                    try {
                      dispatch(setGlobalLoader(true));
                      const res = await getFuelCardApplications('?limit=10000');
                      const allDocs = res?.data?.data?.docs ?? [];
                      const rows = allDocs.map((d, i) => ({
                        'Sr No.': i + 1,
                        Organization: d?.applicantId?.name ?? '—',
                        'Contact Name': d?.contactName ?? '—',
                        'Contact Phone': d?.contactPhone ?? '—',
                        'Contact Email': d?.contactEmail ?? '—',
                        PAN: d?.pan?.number ?? '—',
                        'PAN Image': d?.pan?.imageUrls?.[0] ?? '—',
                        Aadhaar: d?.aadhaar?.number ?? '—',
                        'Aadhaar Image (Front)': d?.aadhaar?.imageUrls?.[0] ?? '—',
                        'Aadhaar Image (Back)': d?.aadhaar?.imageUrls?.[1] ?? '—',
                        'Vehicle RC': d?.vehicleRC?.number ?? '—',
                        'Vehicle RC Image (Front)': d?.vehicleRC?.imageUrls?.[0] ?? '—',
                        'Vehicle RC Image (Back)': d?.vehicleRC?.imageUrls?.[1] ?? '—',
                        Status: STATUS_LABEL[d?.applicationStatus] ?? d?.applicationStatus ?? '—',
                        'Applied On': d?.createdAt ? format(new Date(d.createdAt), 'MMM dd, yyyy') : '—',
                      }));
                      const headers = ['Sr No.', 'Organization', 'Contact Name', 'Contact Phone', 'Contact Email', 'PAN', 'PAN Image', 'Aadhaar', 'Aadhaar Image (Front)', 'Aadhaar Image (Back)', 'Vehicle RC', 'Vehicle RC Image (Front)', 'Vehicle RC Image (Back)', 'Status', 'Applied On'];
                      const linkColumns = ['PAN Image', 'Aadhaar Image (Front)', 'Aadhaar Image (Back)', 'Vehicle RC Image (Front)', 'Vehicle RC Image (Back)'];

                      const worksheet = XLSX.utils.json_to_sheet(rows);
                      XLSX.utils.sheet_add_aoa(worksheet, [headers], { origin: 'A1' });

                      // Set clickable hyperlinks on image URL cells
                      const linkColIndices = headers.reduce((acc, h, i) => {
                        if (linkColumns.includes(h)) acc[i] = h;
                        return acc;
                      }, {});
                      rows.forEach((row, rowIdx) => {
                        Object.entries(linkColIndices).forEach(([colIdx, colName]) => {
                          const url = row[colName];
                          if (url && url !== '—') {
                            const cellRef = XLSX.utils.encode_cell({ r: rowIdx + 1, c: Number(colIdx) });
                            worksheet[cellRef] = { v: 'Link', t: 's', l: { Target: url } };
                          }
                        });
                      });

                      const workbook = XLSX.utils.book_new();
                      XLSX.utils.book_append_sheet(workbook, worksheet, 'Fuel Card Applications');
                      XLSX.writeFile(workbook, 'FuelCardApplications.xlsx', { compression: true });
                      dispatch(setGlobalLoader(false));
                    } catch (error) {
                      dispatch(setGlobalLoader(false));
                      console.log('export error', error);
                      toast.error('Error exporting fuel card applications');
                    }
                  }}
                >
                  Download
                </Button>
                </Box>
              </Stack>
              <TableComponent
                ref={tableRef}
                data={fetchApplications}
                columns={columns}
                title="Fuel Card Applications"
                isRemoteData={true}
              />
            </Box>
          </Box>
        </Box>
      </main>

      {/* View Documents Dialog */}
      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle fontWeight={700}>
          Application Details
          {viewApplication && (
            <Typography variant="body2" color="text.secondary" fontWeight={400}>
              {viewApplication.organizationName} — {viewApplication.contactName}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          {viewApplication && (
            <Box>
              {/* Contact info summary */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="text.secondary">Contact Phone</Typography>
                  <Typography variant="body2" fontWeight={600}>{viewApplication.contactPhone || '—'}</Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="text.secondary">Contact Email</Typography>
                  <Typography variant="body2" fontWeight={600}>{viewApplication.contactEmail || '—'}</Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="text.secondary">Status</Typography>
                  <Box sx={{ mt: 0.5 }}>
                    <Chip
                      label={STATUS_LABEL[viewApplication.applicationStatus] ?? viewApplication.applicationStatus}
                      color={STATUS_COLOR[viewApplication.applicationStatus] ?? 'default'}
                      size="small"
                      sx={{ fontWeight: 700 }}
                    />
                  </Box>
                </Grid>
                {viewApplication.reviewNotes && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">Review Notes</Typography>
                    <Typography variant="body2" fontWeight={600}>{viewApplication.reviewNotes}</Typography>
                  </Grid>
                )}
              </Grid>

              <Divider sx={{ mb: 3 }} />

              {/* Documents */}
              <Grid container spacing={3}>
                <Grid item xs={12} sm={4}>
                  <DocumentCard
                    label="Aadhaar Card"
                    number={viewApplication.aadhaar?.number}
                    imageUrls={viewApplication.aadhaar?.imageUrls}
                    slotLabels={['Front', 'Back']}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <DocumentCard
                    label="PAN Card"
                    number={viewApplication.pan?.number}
                    imageUrls={viewApplication.pan?.imageUrls}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <DocumentCard
                    label="Vehicle RC"
                    number={viewApplication.vehicleRC?.number}
                    imageUrls={viewApplication.vehicleRC?.imageUrls}
                    slotLabels={['Front', 'Back']}
                  />
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Status Update Dialog */}
      <Dialog open={statusDialogOpen} onClose={() => setStatusDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle fontWeight={700}>Update Application Status</DialogTitle>
        <DialogContent>
          {selectedApplication && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {selectedApplication.organizationName} — {selectedApplication.contactName}
              </Typography>
            </Box>
          )}
          <TextField
            select
            label="Status"
            fullWidth
            size="small"
            value={newStatus}
            onChange={(e) => setNewStatus(e.target.value)}
            sx={{ mb: 2, mt: 1 }}
          >
            {Object.entries(FUEL_CARD_APPLICATION_STATUS).map(([key, value]) => (
              <MenuItem key={key} value={value}>
                {STATUS_LABEL[value]}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Review Notes *"
            fullWidth
            size="small"
            multiline
            rows={3}
            placeholder="Enter notes about this status change..."
            value={reviewNotes}
            onChange={(e) => setReviewNotes(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setStatusDialogOpen(false)} disabled={updating}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleStatusUpdate} disabled={updating}>
            {updating ? <CircularProgress size={20} /> : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FuelCardApplications;
